import { calculateWarikan, formatYen, type Participant } from "./split";
import "./style.css";

type FixedRow = {
  id: string;
  name: string;
  amount: string;
};

type GroupRow = {
  id: string;
  name: string;
  count: string;
  amountEach: string;
};

let fixedPeople: FixedRow[] = [];
let groups: GroupRow[] = [];

const $ = <T extends HTMLElement>(sel: string) =>
  document.querySelector(sel) as T;

function uid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseIntOrNull(raw: string): number | null {
  const t = raw.trim().replace(/[,，]/g, "");
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return NaN;
  return n;
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function renderFixed() {
  const list = $("#fixed-list");
  list.innerHTML = "";
  if (fixedPeople.length === 0) {
    list.innerHTML =
      '<li class="empty-hint">固定金額の人を追加できます</li>';
    return;
  }
  fixedPeople.forEach((p, idx) => {
    const li = document.createElement("li");
    li.className = "list-item";
    li.dataset.id = p.id;
    const placeholder = `名前（省略可・固定${idx + 1}）`;
    li.innerHTML = `
      <div class="top">
        <input class="name-input" type="text" inputmode="text" autocomplete="name"
          placeholder="${escapeAttr(placeholder)}" value="${escapeAttr(p.name)}" data-field="name" />
        <button type="button" class="btn btn-danger-ghost" data-action="remove" aria-label="削除">×</button>
      </div>
      <div class="meta">
        <div class="fixed-wrap show" style="flex: 1">
          <input type="number" inputmode="numeric" min="0" step="1"
            placeholder="固定金額" value="${escapeAttr(p.amount)}" data-field="amount" />
          <span class="yen">円</span>
        </div>
      </div>
    `;
    list.appendChild(li);
  });
}

function renderGroups() {
  const list = $("#groups-list");
  list.innerHTML = "";
  if (groups.length === 0) {
    list.innerHTML =
      '<li class="empty-hint">別単価のグループを追加できます</li>';
    return;
  }
  for (const g of groups) {
    const li = document.createElement("li");
    li.className = "list-item";
    li.dataset.id = g.id;
    li.innerHTML = `
      <div class="top">
        <input class="name-input" type="text" placeholder="グループ名（例: 女性）"
          value="${escapeAttr(g.name)}" data-field="name" />
        <button type="button" class="btn btn-danger-ghost" data-action="remove" aria-label="削除">×</button>
      </div>
      <div class="group-grid">
        <div class="field">
          <label>人数</label>
          <input type="number" inputmode="numeric" min="1" step="1"
            placeholder="2" value="${escapeAttr(g.count)}" data-field="count" />
        </div>
        <div class="field">
          <label>1人あたり（円）</label>
          <input type="number" inputmode="numeric" min="0" step="1"
            placeholder="1000" value="${escapeAttr(g.amountEach)}" data-field="amountEach" />
        </div>
      </div>
    `;
    list.appendChild(li);
  }
}

function syncFixedFromDom() {
  const list = $("#fixed-list");
  const next: FixedRow[] = [];
  list.querySelectorAll<HTMLElement>(".list-item").forEach((li) => {
    const id = li.dataset.id!;
    const name =
      (li.querySelector('[data-field="name"]') as HTMLInputElement)?.value ?? "";
    const amount =
      (li.querySelector('[data-field="amount"]') as HTMLInputElement)?.value ??
      "";
    next.push({ id, name, amount });
  });
  fixedPeople = next;
}

function syncGroupsFromDom() {
  const list = $("#groups-list");
  const next: GroupRow[] = [];
  list.querySelectorAll<HTMLElement>(".list-item").forEach((li) => {
    const id = li.dataset.id!;
    const name =
      (li.querySelector('[data-field="name"]') as HTMLInputElement)?.value ?? "";
    const count =
      (li.querySelector('[data-field="count"]') as HTMLInputElement)?.value ?? "";
    const amountEach =
      (li.querySelector('[data-field="amountEach"]') as HTMLInputElement)
        ?.value ?? "";
    next.push({ id, name, count, amountEach });
  });
  groups = next;
}

type BuildOk = {
  ok: true;
  total: number;
  headcount: number;
  participants: Participant[];
  remainderCount: number;
  fixedCount: number;
  groupCount: number;
};

type BuildErr = { ok: false; error: string; soft?: boolean };
type BuildIdle = { ok: false; idle: true; message: string };

function buildInput(): BuildOk | BuildErr | BuildIdle {
  syncFixedFromDom();
  syncGroupsFromDom();

  const totalRaw = ($("#total") as HTMLInputElement).value;
  const headRaw = ($("#headcount") as HTMLInputElement).value;

  if (totalRaw.trim() === "" && headRaw.trim() === "" && fixedPeople.length === 0 && groups.length === 0) {
    return { ok: false, idle: true, message: "①合計金額と②参加人数を入力してください" };
  }

  const total = parseIntOrNull(totalRaw);
  if (totalRaw.trim() === "") {
    return { ok: false, idle: true, message: "①合計金額を入力してください" };
  }
  if (total === null || Number.isNaN(total) || total < 0) {
    return { ok: false, error: "合計金額は0以上の整数で入力してください" };
  }

  const headcount = parseIntOrNull(headRaw);
  if (headRaw.trim() === "") {
    return { ok: false, idle: true, message: "②参加人数を入力してください" };
  }
  if (headcount === null || Number.isNaN(headcount) || headcount < 1) {
    return { ok: false, error: "参加人数は1以上の整数で入力してください" };
  }

  const participants: Participant[] = [];
  let fixedCount = 0;

  for (let i = 0; i < fixedPeople.length; i++) {
    const p = fixedPeople[i];
    const hasAny = p.name.trim() !== "" || p.amount.trim() !== "";
    if (!hasAny) continue;

    if (p.amount.trim() === "") {
      return {
        ok: false,
        soft: true,
        error: `固定の人 #${i + 1} の金額を入力してください`,
      };
    }
    const amt = parseIntOrNull(p.amount);
    if (amt === null || Number.isNaN(amt) || amt < 0) {
      return {
        ok: false,
        error: `固定の人 #${i + 1} の金額は0以上の整数で入力してください`,
      };
    }
    const name = p.name.trim() || `固定${i + 1}`;
    participants.push({ kind: "person", name, fixedAmount: amt });
    fixedCount += 1;
  }

  let groupCount = 0;
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i];
    const name = g.name.trim();
    if (!name && !g.count.trim() && !g.amountEach.trim()) continue;

    if (!name) {
      return { ok: false, soft: true, error: `グループ #${i + 1} の名前を入力してください` };
    }
    if (g.count.trim() === "" || g.amountEach.trim() === "") {
      return {
        ok: false,
        soft: true,
        error: `グループ「${name}」の人数と1人あたり金額を入力してください`,
      };
    }
    const count = parseIntOrNull(g.count);
    const amountEach = parseIntOrNull(g.amountEach);
    if (count === null || Number.isNaN(count) || count < 1) {
      return {
        ok: false,
        error: `グループ「${name}」の人数は1以上の整数で入力してください`,
      };
    }
    if (amountEach === null || Number.isNaN(amountEach) || amountEach < 0) {
      return {
        ok: false,
        error: `グループ「${name}」の1人あたり金額は0以上の整数で入力してください`,
      };
    }
    participants.push({ kind: "group", name, count, amountEach });
    groupCount += count;
  }

  const assigned = fixedCount + groupCount;
  if (assigned > headcount) {
    return {
      ok: false,
      error: `固定・グループの合計（${assigned}人）が参加人数（${headcount}人）を超えています`,
    };
  }

  const remainderCount = headcount - assigned;

  return {
    ok: true,
    total,
    headcount,
    participants,
    remainderCount,
    fixedCount,
    groupCount,
  };
}

function setLiveStatus(html: string) {
  $("#live-status").innerHTML = html;
}

function showError(msg: string) {
  const box = $("#error-box");
  box.textContent = msg;
  box.classList.add("show");
  $("#result").classList.remove("show");
}

function clearError() {
  const box = $("#error-box");
  box.textContent = "";
  box.classList.remove("show");
}

function hideResult() {
  $("#result").classList.remove("show");
}

function renderResult(
  result: Extract<ReturnType<typeof calculateWarikan>, { ok: true }>,
  meta: { headcount: number; fixedCount: number; groupCount: number },
) {
  clearError();
  const el = $("#result");
  el.classList.add("show");

  $("#sum-total").textContent = formatYen(result.total);
  $("#sum-fixed").textContent = formatYen(result.fixedSubtotal);
  $("#sum-leftover").textContent = formatYen(result.leftover);
  $("#sum-remainder-count").textContent =
    result.remainderCount > 0 ? `${result.remainderCount}人` : "0人";

  const heroVal = $("#sum-remainder-each");
  if (result.remainderCount > 0) {
    heroVal.textContent = formatYen(result.remainderEach);
  } else {
    heroVal.textContent = "—";
  }

  const tbody = $("#breakdown-body");
  tbody.innerHTML = "";
  for (const line of result.lines) {
    const tr = document.createElement("tr");
    const roleLabel = line.role === "fixed" ? "固定" : "余り";
    tr.innerHTML = `
      <td><span class="label-cell"><span class="label-name">${escapeAttr(line.label)}</span><span class="role-tag ${line.role}">${roleLabel}</span></span></td>
      <td class="amt">${formatYen(line.amountEach)}</td>
      <td class="amt">${formatYen(line.subtotal)}</td>
    `;
    tbody.appendChild(tr);
  }

  const check = $("#sum-check");
  let html = `<div class="ok">集まる額 ${formatYen(result.collected)}</div>`;
  if (result.overage > 0) {
    html += `<div class="note">切り上げ +${formatYen(result.overage)}</div>`;
  } else if (result.collected === result.total) {
    html += `<div class="note">ぴったり</div>`;
  }
  check.innerHTML = html;

  setLiveStatus(
    `<strong>${meta.headcount}</strong>人（固定${meta.fixedCount}・グループ${meta.groupCount}・余り${result.remainderCount}）`,
  );
}

function recompute() {
  const built = buildInput();

  if (!built.ok) {
    if ("idle" in built) {
      clearError();
      hideResult();
      setLiveStatus(built.message);
      return;
    }
    // BuildErr
    if (built.soft) {
      clearError();
      hideResult();
      setLiveStatus(built.error);
      return;
    }
    showError(built.error);
    setLiveStatus(built.error);
    return;
  }

  const result = calculateWarikan({
    total: built.total,
    participants: built.participants,
    extraRemainderCount: built.remainderCount,
  });

  if (!result.ok) {
    showError(result.error);
    setLiveStatus(result.error);
    return;
  }

  renderResult(result, {
    headcount: built.headcount,
    fixedCount: built.fixedCount,
    groupCount: built.groupCount,
  });
}

function loadExample() {
  ($("#total") as HTMLInputElement).value = "10000";
  ($("#headcount") as HTMLInputElement).value = "7";
  fixedPeople = [
    { id: uid(), name: "A", amount: "2000" },
    { id: uid(), name: "B", amount: "1500" },
  ];
  groups = [{ id: uid(), name: "女性", count: "2", amountEach: "1000" }];
  renderFixed();
  renderGroups();
  recompute();
}

function resetAll() {
  ($("#total") as HTMLInputElement).value = "";
  ($("#headcount") as HTMLInputElement).value = "";
  fixedPeople = [];
  groups = [];
  renderFixed();
  renderGroups();
  clearError();
  hideResult();
  setLiveStatus("入力すると結果が出ます");
}

function bind() {
  $("#add-fixed").addEventListener("click", () => {
    syncFixedFromDom();
    fixedPeople.push({ id: uid(), name: "", amount: "" });
    renderFixed();
    recompute();
    const inputs = $("#fixed-list").querySelectorAll<HTMLInputElement>(
      '[data-field="amount"]',
    );
    inputs[inputs.length - 1]?.focus();
  });

  $("#add-group").addEventListener("click", () => {
    syncGroupsFromDom();
    const groupNumber = groups.length + 1;
    const defaultName =
      groupNumber <= 26
        ? String.fromCharCode(64 + groupNumber) + "グループ"
        : `グループ${groupNumber}`;
    groups.push({ id: uid(), name: defaultName, count: "2", amountEach: "" });
    renderGroups();
    recompute();
    const inputs = $("#groups-list").querySelectorAll<HTMLInputElement>(
      '[data-field="name"]',
    );
    inputs[inputs.length - 1]?.focus();
  });

  $("#fixed-list").addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const btn = t.closest("[data-action='remove']") as HTMLElement | null;
    if (btn) {
      const li = btn.closest(".list-item") as HTMLElement;
      syncFixedFromDom();
      fixedPeople = fixedPeople.filter((p) => p.id !== li.dataset.id);
      renderFixed();
      recompute();
    }
  });

  $("#groups-list").addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const btn = t.closest("[data-action='remove']") as HTMLElement | null;
    if (btn) {
      const li = btn.closest(".list-item") as HTMLElement;
      syncGroupsFromDom();
      groups = groups.filter((g) => g.id !== li.dataset.id);
      renderGroups();
      recompute();
    }
  });

  // Live recompute on any input change
  const wrap = document.querySelector(".wrap")!;
  wrap.addEventListener("input", () => {
    recompute();
  });
  wrap.addEventListener("change", () => {
    recompute();
  });

  $("#example-btn").addEventListener("click", loadExample);
  $("#reset-btn").addEventListener("click", resetAll);
}

function registerSW() {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("./sw.js").catch(() => {
      /* offline / file:// では失敗しうる */
    });
  });
}

renderFixed();
renderGroups();
bind();
registerSW();
recompute();
